import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { compose } from "recompose";
import { setExploreSelectedIds, setExplorePropertyDetailsBoxIsOpen, setExplorePropertyDetailsIsOpen,
	createExploreViewObject, setExploreSelectedObjectId, setExploreViewUnderwriteModalIsOpen, appNotify } from 'store/actions';
import { withStyles } from "@material-ui/core/styles";
import { Tooltip, Typography, IconButton, SvgIcon } from '@material-ui/core';
import VerticalDetailsBox from "../common/VerticalDetailsBox";
import PropertyDetailsBox from "../common/PropertyDetailsBox";
import AmherstIcon from "assets/images/rex/AmherstIcon.png";
import ImageCarousel from 'components/layout/ImageCarousel/ImageCarousel';
import { sendClientLog } from 'services/logService';
import { getGoogleImageUrls, getUWPropertyImage } from 'utilities/urls';
import { Constants } from "utilities/constants";
import ViewUnderwriteModal from 'components/explore/gmap/ViewUnderwriteModal';
import PropertyDetailsPage from "components/explore/propertyDetailsPage";
import { createGUID } from 'utilities';
import { getObjectDefaults } from 'store/explore/views';
import { fields } from "components/explore/resultsGrid/gridHelpers/fields";
import { format, startOfToday, subMonths } from "date-fns";
import { generatePolygonAroundPoint } from "components/explore/gmap/helpers";

const boxFields = ['propertySubtype', 'yearBuilt', 'beds', 'fullBaths', 'livingAreaSqFt', 'forSalePrice'];
const thumbnailImageCount = 6;

function MapPropertyDetailsBox({ classes, user, propertyDetailsData, propertyDetailsBoxIsOpen,
	setExploreSelectedIds, setExplorePropertyDetailsBoxIsOpen, setExplorePropertyDetailsIsOpen, isMobile,
	selectedView, createExploreViewObject, setExploreSelectedObjectId, viewUnderwriteModalIsOpen, 
	setExploreViewUnderwriteModalIsOpen, appNotify }) {
	const [imageSources, setImageSources] = useState([]);   // Images for the album
	const [imagesLoading, setImagesLoading] = useState(true);
	const getGoogleImageSource = (googleImageUrls) => {
		const images = [];
		if (googleImageUrls.streetViewUrl) {
			images.push({
				id: 1,
				caption: 'Street View',
				imageUrl: googleImageUrls.streetViewUrl,
				source: 'Google',
				sortOrder: 1
			});
		}
		images.push({
			id: images.length,
			caption: 'Satellite View',
			imageUrl: googleImageUrls.mapsStaticUrl,
			source: 'Google',
			sortOrder: images.length
		})
		const googleImageSource = {
			source: 'Google',
			images
		}
		return googleImageSource;
	};

	useEffect(() => {
		const visible = propertyDetailsData?.asgPropID != null;
		setExplorePropertyDetailsBoxIsOpen(visible);
		if (!visible) return;
		setImagesLoading(true);
		setImageSources([]);

		(async () => {
			let propertyImageSources = [];
			const uwPropertyImageSources = await getUWPropertyImage(propertyDetailsData.asgPropID, propertyDetailsData.transactionOpportunityId, 'ExplorePropertyDetailsBox');  // Use PIM to get images first then google streetview
			if (uwPropertyImageSources && uwPropertyImageSources.length > 0) {
				propertyImageSources.push(...uwPropertyImageSources);
			}
			if (propertyDetailsData) {
				const googleImageUrls = await getGoogleImageUrls(propertyDetailsData.fullAddress, 900, 550, true);
				propertyImageSources.push(getGoogleImageSource(googleImageUrls));
			}
			setImageSources(propertyImageSources);
			setImagesLoading(false);
		})();
	}, [propertyDetailsData?.asgPropID]);

	function onClose() {
		setExploreSelectedIds(null);
	}

	function onMoreButtonClick() {
		console.log(propertyDetailsData);
		setExplorePropertyDetailsIsOpen(true);
		sendClientLog('Info', `Explore - User opened Property Details page from Explore map for PM ID ${propertyDetailsData?.asgPropID}`);
	}

	function onViewUnderwriteButtonClick() {
		setExploreViewUnderwriteModalIsOpen(true);
	}

	const getFieldMinMax = (field) => {
		const value = propertyDetailsData[field];
		switch (field) {
			case 'beds':
				return {
					min: Math.max(1, value - 1),
					max: value + 1
				}
			case 'fullBaths':
				return {
					min: Math.max(1, value - 1),
					max: value + 1
				}
			case 'livingAreaSqFt':
				return {
					min: Math.round(value * 0.85),
					max: Math.round(value * 1.15)
				}
			case 'yearBuilt':
				const getMinYearBuilt = yearBuilt => {
					if (yearBuilt >= 1980) return yearBuilt - 10;
					if (yearBuilt >= 1970 && yearBuilt <= 1979) return 1965;
					if (yearBuilt >= 1960 && yearBuilt <= 1969) return 1955;
					if (yearBuilt >= 1950 && yearBuilt <= 1959) return 1940;
					if (yearBuilt >= 1940 && yearBuilt <= 1949) return 1930;
					if (yearBuilt >= 1930 && yearBuilt <= 1939) return 1920;
					if (yearBuilt >= 1800 && yearBuilt <= 1929) return 1800;
					return 0;
				};
				const getMaxYearBuilt = yearBuilt => {
					if (yearBuilt >= 1960) return Math.min(yearBuilt + 10, new Date().getFullYear() + 1);
					if (yearBuilt <= 1959) return yearBuilt + 20;
					return 0;
				}

				return {
					min: getMinYearBuilt(value),
					max: getMaxYearBuilt(value)
				}
			default: return null;
		}
	}

	const handleCompsPdoClick = () => {
		const layerId = selectedView?.objects.find(obj => obj.id === propertyDetailsData.objectId)?.layerId;
		if (layerId == null) return;
		const pdoType = Constants.ViewsObjectType.PROPERTYDATA;
		const objectCount = selectedView.objects.filter(object => object.type === pdoType).length;
		const countLimitReached = objectCount >= Constants.ViewsObjectCountLimit[pdoType];
		if (countLimitReached) {
			appNotify('Quantity limit reached. Remove a Property Data object and try again', 'warning');
			return;
		}

		const filterFields = ['beds', 'fullBaths', 'livingAreaSqFt', 'yearBuilt'].filter(field => propertyDetailsData[field] != null);
		if (!filterFields.length) {
			appNotify("Unable to create comps from this property's data", 'warning');
			return;
		}
		const statusValues = ['For Sale and For Rent', 'For Rent', 'Rented', 'Off Market (Rental)'];

		const getFilter = (isFilterExpression) => {
			const filter = [['asgPropID', '<>', propertyDetailsData.asgPropID]];
			filterFields.forEach(field => {
				filter.push('and');
				const fieldMinMax = getFieldMinMax(field);
				if (isFilterExpression) {
					filter.push([
						[field, '>=', fieldMinMax.min],
						'and',
						[field, '<=', fieldMinMax.max]
					]);
				} else {
					filter.push([field, 'between', [fieldMinMax.min, fieldMinMax.max]])
				}
			});
			let statusFilter = [];
			if (isFilterExpression) {
				statusValues.forEach(val => {
					statusFilter.push(['status', '=', val], 'or');
				});
				statusFilter.pop();
			} else {
				statusFilter = ['status', 'anyof', statusValues];
			}
			filter.push('and', statusFilter);
			const twelveMonthsAgo = format(subMonths(startOfToday(), 12), "yyyy-MM-dd'T'HH:mm:ss.SSS");
			filter.push('and', ['statusDate', '>=', twelveMonthsAgo]);
			return filter;
		}
		
		const { groupLevel, pinColorIds, pinColorPaletteId } = getObjectDefaults(pdoType);
		const filter = {
			filterValue: getFilter(),
			filterExpression: getFilter(true)
		}
		const newPinColorIds = {}
		Object.keys(pinColorIds).forEach(key => newPinColorIds[key] = 'lightGreen');

		const propertyDataObj = {
			visible: true,
			description: `Comps for ${propertyDetailsData.address}`,
			groupLevel,
			pinDisplayType: 'lastRent',
			pinColorIds: newPinColorIds,
			pinColorPaletteId,
			filter,
			filterGeo: generatePolygonAroundPoint(propertyDetailsData.latitude, propertyDetailsData.longitude, 2, 10),
			isFilterSet: true,
			isCompsPdo: true
		}
		const newObjId = 'tempObject-' + createGUID();
		createExploreViewObject(newObjId, layerId, pdoType, propertyDataObj);
		appNotify('Property Data object created', 'success');
		setExploreSelectedObjectId(null);
		// highlightExploreViewObject(newObjId, pdoType);
	}

	//data for testing - uncomment to see prop details popup right away
	//propertyDetailsData = JSON.parse(`{"Id":00000,"Address":xxxxxxxxxxx"}`);

	if (propertyDetailsData && propertyDetailsBoxIsOpen) {
		if (isMobile) {
			return (
				<>
					<PropertyDetailsBox
						contentPadding={10}
						propertyDetailsData={propertyDetailsData}                        
						title={propertyDetailsData.address}                    
						//headerImageURL={`http://maps.googleapis.com/maps/api/streetview?size=250x150&sensor=false&key=${process.env.REACT_APP_GMAPS_STREETVIEW_API}&location=${propertyDetailsData.fullAddress}`}
						headerElement={
							<div style={{ width: '100%', padding: 10 }}>
								{
									imagesLoading &&
									<div style={{ padding: 5, backgroundColor: 'white', alignSelf: 'center', alignItems: 'center' }}>
										<Typography variant="caption" noWrap={true}>Loading Images ...</Typography>
									</div>
								}
								{imageSources && imageSources.length > 0 && <ImageCarousel square imageSources={imageSources} showAsGrid gridThumbnailCount={thumbnailImageCount} />}
							</div>
						}
						hasMoreButton
						onMoreButtonClick={onMoreButtonClick}
					/>
					{
						propertyDetailsData.isAmherstManaged &&
						<Tooltip title={"Amherst Property"} placement="top">
							<img src={AmherstIcon} alt="Amherst Property" className={classes.amherstIcon} />
						</Tooltip>
					}
					<svg height="20" width="20" className={classes.closeIcon} onClick={onClose}>
						<path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
					</svg>
					<PropertyDetailsPage isMobile={true} handleCompsPdoClick={handleCompsPdoClick}/>
				</>
			)
		} else {
			return (
				<>
					<VerticalDetailsBox
						contentPadding={8}
						dataItem={propertyDetailsData}
						fields={boxFields.map(el => fields.find(field => field.dataField === el))}
						title={propertyDetailsData.address}                    
						//headerImageURL={`http://maps.googleapis.com/maps/api/streetview?size=250x150&sensor=false&key=${process.env.REACT_APP_GMAPS_STREETVIEW_API}&location=${propertyDetailsData.fullAddress}`}
						headerElement={
							<div style={{ maxWidth: 260, padding: 10 }}>
								{
									imagesLoading &&
									<div style={{ paddingTop: 16, backgroundColor: 'white', textAlign: 'center' }}>
										<Typography variant="caption" noWrap={true}>Loading Images ...</Typography>
									</div>
								}
								{imageSources && imageSources.length > 0 && <ImageCarousel square imageSources={imageSources} showAsGrid gridThumbnailCount={thumbnailImageCount} />}
							</div>
						}
						footerElement={
							<div className={classes.detailsButtonContainer}>
								<Tooltip title='View property details'>
									<IconButton onClick={onMoreButtonClick}>
										<SvgIcon>
											<path d="M10,21V19H6.41L10.91,14.5L9.5,13.09L5,17.59V14H3V21H10M14.5,10.91L19,6.41V10H21V3H14V5H17.59L13.09,9.5L14.5,10.91Z" />
										</SvgIcon>
									</IconButton>
								</Tooltip> 
								<Tooltip title='View comps as a Property Data object'>
									<IconButton onClick={handleCompsPdoClick}>
										<SvgIcon>
											<path d='M19.31 18.9C19.75 18.21 20 17.38 20 16.5C20 14 18 12 15.5 12S11 14 11 16.5 13 21 15.5 21C16.37 21 17.19 20.75 17.88 20.32L21 23.39L22.39 22L19.31 18.9M15.5 19C14.12 19 13 17.88 13 16.5S14.12 14 15.5 14 18 15.12 18 16.5 16.88 19 15.5 19M5 20V12H2L12 3L22 12H20.18C19 10.77 17.34 10 15.5 10C11.92 10 9 12.92 9 16.5C9 17.79 9.38 19 10.03 20H5Z' />
										</SvgIcon>
									</IconButton>
								</Tooltip>
								{(propertyDetailsData.transactionOpportunityId != null || propertyDetailsData.postAcqTaskId != null) && user.roles.underwriteUser &&
									<Tooltip title='View underwrite'>
										<IconButton onClick={onViewUnderwriteButtonClick}>
											<SvgIcon>
												<path d="M11 8C11 10.21 9.21 12 7 12C4.79 12 3 10.21 3 8C3 5.79 4.79 4 7 4C9.21 4 11 5.79 11 8M11 14.72V20H0V18C0 15.79 3.13 14 7 14C8.5 14 9.87 14.27 11 14.72M24 20H13V3H24V20M16 11.5C16 10.12 17.12 9 18.5 9C19.88 9 21 10.12 21 11.5C21 12.88 19.88 14 18.5 14C17.12 14 16 12.88 16 11.5M22 7C20.9 7 20 6.11 20 5H17C17 6.11 16.11 7 15 7V16C16.11 16 17 16.9 17 18H20C20 16.9 20.9 16 22 16V7Z" />
											</SvgIcon>
										</IconButton>
									</Tooltip>
								}        
							</div>
						}
						onMoreButtonClick={onMoreButtonClick}
						onViewUnderwriteButtonClick={onViewUnderwriteButtonClick}
					/>
					{
						propertyDetailsData.isAmherstManaged &&
						<Tooltip title={"Amherst Property"} placement="top">
							<img src={AmherstIcon} alt="Amherst Property" className={classes.amherstIcon} />
						</Tooltip>
					}
					<svg height="20" width="20" className={classes.closeIcon} onClick={onClose}>
						<path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
					</svg>
					<ViewUnderwriteModal
						open={viewUnderwriteModalIsOpen}
						handleClose={() => setExploreViewUnderwriteModalIsOpen(false)}
						txnOppId={propertyDetailsData.transactionOpportunityId}
						asgPropID={propertyDetailsData.asgPropID}
					/>
					<PropertyDetailsPage handleCompsPdoClick={handleCompsPdoClick}/>
				</>
			)
		}
	} else {
		return (
			<>
			</>
		)
	}
}

const styles = theme => ({
	amherstIcon: {
		position: 'absolute',
		left: 5,
		top: 15,
		verticalAlign: 'middle',
		borderRadius: `20%`,
		boxShadow: `0px 0px 5px 0px rgba(255, 255, 255, 0.6)`
	},
	closeIcon: {
		position: 'absolute',
		right: 0,
		top: 0,
		width: 25,
		height: 25,
		borderRadius: `0px 4px 0px 0px`,
		backgroundColor: 'rgba(255, 255, 255, 1)',
		"&:hover": {
			backgroundColor: 'rgba(212, 212, 212, 1)',
			cursor: `pointer`
		}
	},
	detailsButtonContainer: {
		margin: '0 auto',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingBottom: 8
	}
});

function mapState(state) {
	const { exploreViews, exploreData, exploreMap, appState } = state;
	return {
		user: appState.user,
		propertyDetailsBoxIsOpen: exploreMap.propertyDetailsBoxIsOpen,
		propertyDetailsData: exploreData.propertyDetailsData,
		propertyData: exploreData.propertyData,
		isMobile: appState.isMobile,
		selectedView: exploreViews.selectedView,
		viewUnderwriteModalIsOpen: exploreMap.viewUnderwriteModalIsOpen
	};
}

const mapDispatch = {
	setExploreSelectedIds,
	setExplorePropertyDetailsBoxIsOpen,
	setExplorePropertyDetailsIsOpen,
	createExploreViewObject,
	setExploreSelectedObjectId,
	setExploreViewUnderwriteModalIsOpen,
	appNotify
}

export default compose(
	withStyles(styles),
	connect(mapState, mapDispatch)
)(MapPropertyDetailsBox);
